import { Divider, Stack, Typography } from '@mui/material'
import numeral from 'numeral'
import { Fragment, useMemo } from 'react'
import { Button, IconButton } from 'utoniq-ui'

import { ItemDetailBoxFragment, Maybe } from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { ResaleItem } from '@/components/organisms'
import { StorageImage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { generateCreditCardLogoPath } from '@/core/creditCard'
import { getItemSkuPrice } from '@/core/sku'
import { getHelpUrl } from '@/lib/helpUrl'

export * from './fragments'

export type State = {
  selectedCount: number
  cardNumber?: string
  brand?: string
  item: Maybe<ItemDetailBoxFragment> | undefined
  submitting: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: () => Promise<void>
}

export const ResaleApplicationCreateConfirmationTemplate: Template<
  State,
  Action
> = ({ state, onSubmit }) => {
  const { t } = useTranslation('resaleApplicationCreateConfirmation')
  const { onClick: onClickHelp } = useBrowserLink(getHelpUrl('resaleApply'))
  const { routerPages, router, generateQueryPath } = useRouter<{
    itemId: string
    skuId?: string
  }>()

  return (
    <>
      <BaseLayout
        margin
        header={{
          back: true,
          title: t('title', 'リセール応募'),
          defaultBackPage: generateQueryPath(
            routerPages.ResaleApplicationCreate,
            { itemId: router.query.itemId },
            { skuId: router.query.skuId ?? '' }
          ),
          right: (
            <IconButton.Help onClick={onClickHelp} edge="end" color="primary" />
          ),
        }}
      >
        <Typography variant="h4" sx={{ py: 5 }}>
          {t('resaleInfo', '最終確認')}
        </Typography>
        <ApplicationProceedConfirmationContentCell
          submitting={state.submitting}
          item={state.item}
          brand={state.brand}
          selectedCount={state.selectedCount}
          cardNumber={state.cardNumber}
          onSubmit={onSubmit}
        />
      </BaseLayout>
    </>
  )
}

type ApplicationProceedConfirmationContentCellProps = {
  item: State['item']
  cardNumber: State['cardNumber']
  brand: State['brand']
  selectedCount: State['selectedCount']
  submitting: State['submitting']
  onSubmit: Action['onSubmit']
}
const ApplicationProceedConfirmationContentCell: Cell<ApplicationProceedConfirmationContentCellProps> =
  ({ item, selectedCount, cardNumber, brand, submitting, onSubmit }) => {
    const { t } = useTranslation('resaleApplicationCreateConfirmation')
    const { router, routerPages, generateQueryPath } = useRouter<{
      itemId: string
    }>()
    const purchaseFee = item?.metadata?.ticket?.resaleFee?.purchaseFee || 0
    const { price, formattedPrice } = useMemo(
      () => getItemSkuPrice(item),
      [item]
    )

    const totalAmount = useMemo(() => {
      if (!price) {
        return 0
      }
      return numeral(price * selectedCount + purchaseFee).format('0,0')
    }, [purchaseFee, price, selectedCount])

    const data = [
      {
        label: t('ticketPrice', '代金'),
        value: `￥${numeral((price || 0) * selectedCount).format('0,0')}`,
      },
      {
        label: t('transferFee', 'システム手数料'),
        value: `¥${numeral(purchaseFee).format('0,0')}`,
      },
    ]
    return (
      <CellFrame>
        <ResaleItem title={t('applicationInfo', '応募情報')}>
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography variant="subtitle1">
                {t('ticketName', 'チケット名')}
              </Typography>
              <Typography variant="body2">{item?.title}</Typography>
              {!!item?.sku?.unitName && (
                <Typography variant="body2">{item?.sku?.unitName}</Typography>
              )}
            </Stack>
            <Divider />
            <Stack spacing={1}>
              <Typography variant="subtitle1">
                {t('ticketPriceLabel', '価格')}
              </Typography>
              <Typography variant="body2">
                {t('ticketPriceValue', '¥{{price}}/1枚あたり', {
                  price: formattedPrice || 0,
                })}
              </Typography>
            </Stack>
            <Divider />
            <Stack spacing={1}>
              <Typography variant="subtitle1">
                {t('applicationNumber', '応募枚数')}
              </Typography>
              <Typography variant="body2">
                {selectedCount}
                {t('sheet', '枚')}
              </Typography>
            </Stack>
          </Stack>
        </ResaleItem>
        <ResaleItem title={t('paymentInfo', '支払い情報')}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="subtitle1" color="text.secondary">
              {t('creditCardInfo', 'クレジットカード情報')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={3}>
            <StorageImage
              width={40}
              height={32}
              src={generateCreditCardLogoPath(brand)}
              target="common"
            />
            <Typography variant="body2">
              {t('last4Digit', { number: cardNumber })}
            </Typography>
          </Stack>
        </ResaleItem>
        <ResaleItem title={t('totalPayment', 'お支払い総額')}>
          <Stack spacing={3}>
            <Stack>
              {data.map(({ value, label }, index) => (
                <Fragment key={index}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ py: 2 }}
                  >
                    <Typography variant="subtitle1" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="body2">{value}</Typography>
                  </Stack>
                  <Divider />
                </Fragment>
              ))}
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ py: 2 }}
              >
                <Typography variant="subtitle1" color="text.secondary">
                  {t('total', '合計')}
                </Typography>
                <Typography variant="h6">¥{totalAmount}</Typography>
              </Stack>
            </Stack>
          </Stack>
          <Typography variant="body2">
            {t(
              'resaleApplicationNote',
              '応募のみではお支払いは発生しません。\n抽選にて当選後、申請された場合のみ請求されます。'
            )}
          </Typography>
        </ResaleItem>

        <Stack spacing={4} alignItems="center" sx={{ pb: 6 }}>
          <Button
            size="large"
            sx={{ width: 212 }}
            onClick={onSubmit}
            loading={submitting}
            disabled={!selectedCount || !cardNumber}
          >
            {t('submit', '応募する')}
          </Button>
          <Button
            variant="text"
            sx={{ width: 212 }}
            onClick={() =>
              router.goBack(
                generateQueryPath(
                  routerPages.ResaleApplicationCreate,
                  { itemId: router.query.itemId },
                  { skuId: decodeURI(item?.sku?.id ?? '') }
                )
              )
            }
          >
            {t('back', '戻る')}
          </Button>
        </Stack>
      </CellFrame>
    )
  }
